import * as THREE from 'three';
import { CharacterRig } from './CharacterRig';
import {
  CHARACTER_HEIGHT,
  CHARACTER_RADIUS,
} from './characterSize';
import { Input } from './Input';
import { AABB, Climbable, Interactable, pointInAABB } from './types';

/**
 * Collision capsule matched to Mixamo visual (~CHARACTER_HEIGHT).
 *
 * Climb: ICO-style ladders — mount near + face / E / walk into, W/S along axis,
 * clean top/bottom dismount. Space is jump (or leap off while climbing).
 */

const RADIUS = CHARACTER_RADIUS;
const HEIGHT = CHARACTER_HEIGHT;
const SKIN = 0.025;
const GRAVITY = 24;
const WALK_SPEED = 3.2;
const RUN_SPEED = 5.4;
const JUMP_VELOCITY = 6.8;
const CLIMB_SPEED = 2.2;
const STEP_HEIGHT = 0.32;
const GROUND_SNAP = 0.1;
const MAX_SUBSTEP = 1 / 90;
const MOUNT_FACE_DOT = 0.35;
const LADDER_STANDOFF = RADIUS + 0.12;
/** Seconds for top haul (ClimbOff clip or Jump fallback). */
const CLIMB_OFF_DURATION = 0.7;
const SHIMMY_SPEED = 1.55;
const WALL_PRESS_DOT = 0.55;

export class Player {
  readonly rig = new CharacterRig();
  readonly position = new THREE.Vector3();
  velocity = new THREE.Vector3();
  yaw = 0;
  onGround = false;
  climbing = false;
  private climbTarget: Climbable | null = null;
  /** Top-of-ladder haul in progress (blocks input briefly). */
  private climbOffTimer = 0;
  private climbOffFrom = new THREE.Vector3();
  private climbOffTo = new THREE.Vector3();
  private jumpBuffered = false;
  private wallNx = 0;
  private wallNz = 0;
  private shimmying = false;
  private wasAirborne = false;
  private idleFidget = 0;
  private nearClimb: Climbable | null = null;
  private coyote = 0;
  private readonly spawnPoint = new THREE.Vector3();
  private killY = -3.5;
  private companionHeld = false;

  constructor(spawn: THREE.Vector3, killY = -3.5) {
    this.spawnPoint.copy(spawn);
    this.killY = killY;
    this.position.copy(spawn);
    this.position.y = Math.max(spawn.y, 0);
    this.onGround = true;
    this.rig.root.position.copy(this.position);
  }

  setCompanionHeld(v: boolean): void {
    this.companionHeld = v;
    this.rig.setHeldMode(v);
    if (v) {
      this.rig.beginTakeHand();
    } else {
      const s = this.rig.getState();
      if (
        s === 'holdIdle' ||
        s === 'holdWalk' ||
        s === 'holdRun' ||
        s === 'takeHand'
      ) {
        this.rig.setState(this.climbing ? 'climb' : this.onGround ? 'idle' : 'fall');
      }
    }
  }

  isCompanionHeld(): boolean {
    return this.companionHeld;
  }

  /** Soft catch / void respawn when falling out of the keep. */
  respawnAtStart(): void {
    this.climbing = false;
    this.climbTarget = null;
    this.climbOffTimer = 0;
    this.velocity.set(0, 0, 0);
    this.jumpBuffered = false;
    this.coyote = 0;
    this.position.copy(this.spawnPoint);
    this.position.y = Math.max(this.spawnPoint.y, 0);
    this.onGround = true;
    this.rig.setState('idle');
    this.rig.root.position.copy(this.position);
  }

  checkWorldBounds(killY?: number): boolean {
    const floor = killY ?? this.killY;
    if (this.position.y < floor) {
      this.respawnAtStart();
      return true;
    }
    return false;
  }

  getNearClimb(): Climbable | null {
    return this.nearClimb;
  }

  update(
    dt: number,
    input: Input,
    camYaw: number,
    solids: AABB[],
    climbables: Climbable[],
    /** Rising-edge E reserved for ladder when lever/hand did not claim it. */
    interactMount = false,
  ): void {
    if (input.jumpJustPressed()) this.jumpBuffered = true;

    this.nearClimb = this.findClimb(climbables);

    if (this.climbOffTimer > 0) {
      this.updateClimbOff(dt, solids);
    } else if (this.climbing && this.climbTarget) {
      this.updateClimb(dt, input, solids);
    } else if (
      this.nearClimb &&
      this.wantsMount(this.nearClimb, input, camYaw, interactMount)
    ) {
      this.beginClimb(this.nearClimb);
    } else {
      this.updateMove(dt, input, camYaw, solids, climbables);
    }

    // Soft anti-stuck: if deeply intersecting, push out.
    if (!this.climbing && this.overlapsAny(solids, this.bodyAABB())) {
      this.depenetrate(solids);
    }

    this.checkWorldBounds();

    this.rig.root.position.copy(this.position);
    this.rig.root.rotation.y = this.yaw;
    const horiz = Math.hypot(this.velocity.x, this.velocity.z);
    this.rig.update(dt, horiz);
  }

  /** Mount: E (if free), or walk/forward into ladder while roughly facing it. */
  private wantsMount(
    c: Climbable,
    input: Input,
    camYaw: number,
    interactMount: boolean,
  ): boolean {
    if (!this.onApproachSide(c)) return false;

    if (interactMount) return true;

    const axis = input.axis();
    const pressingIn = axis.z < -0.45;
    if (!pressingIn) return false;
    if (!this.facingLadder(c, camYaw)) return false;
    return true;
  }

  private onApproachSide(c: Climbable): boolean {
    const dx = this.position.x - c.centerX;
    const dz = this.position.z - c.centerZ;
    return dx * c.normal.x + dz * c.normal.z >= -0.15;
  }

  private facingLadder(c: Climbable, camYaw: number): boolean {
    // Prefer camera-forward into the wall (-normal).
    const fx = -Math.sin(camYaw);
    const fz = -Math.cos(camYaw);
    const intoWall = fx * -c.normal.x + fz * -c.normal.z;
    if (intoWall >= MOUNT_FACE_DOT) return true;
    // Also accept body facing (Mixamo +Z).
    const bx = Math.sin(this.yaw);
    const bz = Math.cos(this.yaw);
    return bx * -c.normal.x + bz * -c.normal.z >= MOUNT_FACE_DOT;
  }

  private beginClimb(c: Climbable): void {
    this.climbing = true;
    this.climbTarget = c;
    this.velocity.set(0, 0, 0);
    this.jumpBuffered = false;
    this.onGround = false;
    this.rig.setState('climb');
    const n = c.normal;
    this.position.x = c.centerX + n.x * LADDER_STANDOFF;
    this.position.z = c.centerZ + n.z * LADDER_STANDOFF;
    this.position.y = Math.min(
      Math.max(this.position.y, c.bottomY),
      c.topY - 0.05,
    );
    // Mixamo Climb faces +Z; face *away* from wall (+normal) so hands meet the ladder.
    // (Facing -normal put the back to the rungs.)
    this.yaw = Math.atan2(n.x, n.z);
  }

  private updateClimb(dt: number, input: Input, solids: AABB[]): void {
    const c = this.climbTarget!;
    const axis = input.axis();
    // W (axis.z < 0) climbs up; S climbs down.
    this.position.y += -axis.z * CLIMB_SPEED * dt;
    this.position.x = c.centerX + c.normal.x * LADDER_STANDOFF;
    this.position.z = c.centerZ + c.normal.z * LADDER_STANDOFF;
    this.position.y = Math.max(c.bottomY, Math.min(c.topY + 0.02, this.position.y));
    this.yaw = Math.atan2(c.normal.x, c.normal.z);
    this.rig.setState('climb');

    // Top dismount — play ClimbOff if packed, then step onto ledge.
    if (this.position.y >= c.topY - 0.04 && axis.z < -0.15) {
      this.dismountTop(c, solids);
      return;
    }
    if (this.position.y >= c.topY - 0.01) {
      this.dismountTop(c, solids);
      return;
    }

    // Bottom dismount — step off to ground.
    if (axis.z > 0.55 && this.position.y <= c.bottomY + 0.12) {
      this.climbing = false;
      this.climbTarget = null;
      this.position.y = c.bottomY;
      this.onGround = true;
      this.velocity.set(0, 0, 0);
      this.rig.setState('idle');
      return;
    }

    // Space leaps off backward (away from wall).
    if (this.jumpBuffered) {
      this.jumpBuffered = false;
      this.climbing = false;
      this.climbTarget = null;
      this.velocity.y = JUMP_VELOCITY * 0.65;
      this.velocity.x = c.normal.x * 2.5;
      this.velocity.z = c.normal.z * 2.5;
      this.rig.setState('jump');
    }
  }

  private dismountTop(c: Climbable, solids: AABB[]): void {
    this.climbOffFrom.copy(this.position);
    this.climbOffTo.set(
      c.centerX - c.normal.x * (RADIUS + 0.35),
      c.topY + SKIN,
      c.centerZ - c.normal.z * (RADIUS + 0.35),
    );
    this.climbing = false;
    this.climbTarget = null;
    this.onGround = true;
    this.velocity.set(0, 0, 0);
    this.climbOffTimer = CLIMB_OFF_DURATION;
    // Face onto the ledge (into the platform / -normal).
    this.yaw = Math.atan2(-c.normal.x, -c.normal.z);
    this.rig.setState('climbOff');
    void solids;
  }

  private updateClimbOff(dt: number, solids: AABB[]): void {
    this.climbOffTimer = Math.max(0, this.climbOffTimer - dt);
    const u = 1 - this.climbOffTimer / CLIMB_OFF_DURATION;
    // Ease-out haul onto the ledge
    const t = 1 - (1 - u) * (1 - u);
    this.position.lerpVectors(this.climbOffFrom, this.climbOffTo, t);
    this.velocity.set(0, 0, 0);
    this.onGround = true;
    this.rig.setState('climbOff');

    if (this.climbOffTimer <= 0) {
      this.position.copy(this.climbOffTo);
      this.depenetrate(solids);
      this.rig.setState('idle');
    }
  }

  private updateMove(
    dt: number,
    input: Input,
    camYaw: number,
    solids: AABB[],
    climbables: Climbable[],
  ): void {
    const axis = input.axis();
    const running = input.running();
    const speed = running ? RUN_SPEED : WALK_SPEED;

    // Camera-relative wish — forward is away from camera on XZ.
    const forward = new THREE.Vector3(-Math.sin(camYaw), 0, -Math.cos(camYaw));
    const right = new THREE.Vector3(Math.cos(camYaw), 0, -Math.sin(camYaw));
    const wish = new THREE.Vector3()
      .addScaledVector(right, axis.x)
      .addScaledVector(forward, -axis.z);

    const wishLen = Math.sqrt(wish.lengthSq());
    if (wishLen > 1e-6) {
      wish.multiplyScalar(1 / wishLen);
    }

    // Wall shimmy: keep pressing into a blocking wall → face it and slide sideways.
    const wall = this.onGround ? this.findPressedWall(solids, wish, wishLen) : null;
    if (wall && wishLen > 0.25) {
      this.shimmying = true;
      this.wallNx = wall.nx;
      this.wallNz = wall.nz;
      // Face the wall
      this.yaw = Math.atan2(-wall.nx, -wall.nz);
      const tx = -wall.nz;
      const tz = wall.nx;
      // Strafe along wall from camera right + leftover wish
      let side = axis.x;
      if (Math.abs(side) < 0.2) {
        side = wish.x * tx + wish.z * tz;
      }
      // Don't push through wall; only tangent motion
      this.velocity.x = tx * side * SHIMMY_SPEED;
      this.velocity.z = tz * side * SHIMMY_SPEED;
    } else {
      this.shimmying = false;
      if (wishLen > 1e-6) {
        this.velocity.x = wish.x * speed;
        this.velocity.z = wish.z * speed;
        this.yaw = Math.atan2(wish.x, wish.z);
      } else {
        this.velocity.x *= Math.pow(0.001, dt);
        this.velocity.z *= Math.pow(0.001, dt);
        if (Math.hypot(this.velocity.x, this.velocity.z) < 0.04) {
          this.velocity.x = 0;
          this.velocity.z = 0;
        }
      }
    }

    if (this.jumpBuffered && (this.onGround || this.coyote > 0)) {
      this.velocity.y = JUMP_VELOCITY;
      this.onGround = false;
      this.coyote = 0;
      this.jumpBuffered = false;
      this.shimmying = false;
      this.rig.setState('jump');
    } else {
      this.jumpBuffered = false;
    }

    if (this.onGround && this.velocity.y <= 0) {
      this.velocity.y = 0;
    } else {
      this.velocity.y -= GRAVITY * dt;
      if (this.velocity.y < -28) this.velocity.y = -28;
    }

    const speedXZ = Math.hypot(this.velocity.x, this.velocity.z);
    const sub = Math.min(MAX_SUBSTEP, 1 / (60 + speedXZ * 8));
    let remaining = dt;
    while (remaining > 1e-7) {
      const step = Math.min(sub, remaining);
      remaining -= step;
      this.integrate(step, solids);
    }

    this.probeGround(solids);

    // Air-grab: walk into ladder mid-fall.
    if (!this.onGround && this.velocity.y < 0) {
      const grab = this.findClimb(climbables);
      if (grab && axis.z < -0.25 && this.onApproachSide(grab)) {
        this.beginClimb(grab);
        return;
      }
    }

    if (this.onGround) this.coyote = 0.12;
    else this.coyote = Math.max(0, this.coyote - dt);

    const horiz = Math.hypot(this.velocity.x, this.velocity.z);
    if (this.rig.isTestLocked()) {
      // Anim tester owns the pose.
    } else if (this.rig.isTakingHand() && this.onGround) {
      // Stay in takeHand until timer ends.
    } else if (!this.onGround) {
      this.wasAirborne = true;
      this.rig.setState(this.velocity.y > 0.35 ? 'jump' : 'fall');
    } else if (this.wasAirborne) {
      this.wasAirborne = false;
      this.rig.setState('land');
      this.idleFidget = 0;
    } else if (this.shimmying) {
      if (horiz > 0.12) {
        const tx = -this.wallNz;
        const tz = this.wallNx;
        const along = this.velocity.x * tx + this.velocity.z * tz;
        this.rig.setState(along >= 0 ? 'shimmyR' : 'shimmyL');
      } else {
        this.rig.setState('wallIdle');
      }
    } else if (input.keys.has('KeyC')) {
      this.rig.setState(horiz > 0.25 ? 'sneak' : 'crouch');
      if (horiz > 0.25) {
        this.velocity.x *= 0.55;
        this.velocity.z *= 0.55;
      }
    } else if (this.companionHeld) {
      if (horiz > 0.35) {
        this.rig.setState(running ? 'holdRun' : 'holdWalk');
      } else {
        this.rig.setState('holdIdle');
      }
    } else if (horiz > 0.35) {
      this.idleFidget = 0;
      this.rig.setState(running ? 'run' : 'walk');
    } else {
      this.idleFidget += dt;
      const st = this.rig.getState();
      const holdingPose =
        st === 'land' ||
        st === 'lookAround' ||
        st === 'yawn' ||
        st === 'stretch' ||
        st === 'breathe' ||
        st === 'think' ||
        st === 'wave' ||
        st === 'pull' ||
        st === 'victory';
      if (this.idleFidget > 5.5) {
        this.idleFidget = 0;
        const fidgets = ['lookAround', 'yawn', 'stretch', 'breathe', 'think'] as const;
        this.rig.setState(fidgets[Math.floor(Math.random() * fidgets.length)]!);
      } else if (!holdingPose) {
        this.rig.setState('idle');
      }
    }
  }

  /** Wall we're jammed into while pressing forward into it. */
  private findPressedWall(
    solids: AABB[],
    wish: THREE.Vector3,
    wishLen: number,
  ): { nx: number; nz: number } | null {
    if (wishLen < 0.25) return null;
    // Probe slightly ahead of torso in wish direction
    const probe = this.torsoAABB();
    const reach = RADIUS + SKIN + 0.08;
    probe.minX += wish.x * reach;
    probe.maxX += wish.x * reach;
    probe.minZ += wish.z * reach;
    probe.maxZ += wish.z * reach;

    let best: AABB | null = null;
    let bestDot = WALL_PRESS_DOT;
    for (const s of solids) {
      if (!this.overlaps(probe, s)) continue;
      const topThickness = s.maxY - s.minY;
      // Ignore floors / thin ledges — want upright walls
      if (topThickness < 0.9) continue;
      if (s.maxY < this.position.y + 0.45) continue;
      const cx = (s.minX + s.maxX) * 0.5;
      const cz = (s.minZ + s.maxZ) * 0.5;
      let nx = this.position.x - cx;
      let nz = this.position.z - cz;
      // Prefer axis-aligned push-out normal
      const ox = Math.min(probe.maxX - s.minX, s.maxX - probe.minX);
      const oz = Math.min(probe.maxZ - s.minZ, s.maxZ - probe.minZ);
      if (ox < oz) {
        nx = this.position.x < cx ? -1 : 1;
        nz = 0;
      } else {
        nx = 0;
        nz = this.position.z < cz ? -1 : 1;
      }
      const into = wish.x * -nx + wish.z * -nz;
      if (into > bestDot) {
        bestDot = into;
        best = s;
        this.wallNx = nx;
        this.wallNz = nz;
      }
    }
    if (!best) return null;
    return { nx: this.wallNx, nz: this.wallNz };
  }

  private integrate(dt: number, solids: AABB[]): void {
    this.moveHorizontal('x', this.velocity.x * dt, solids);
    this.moveHorizontal('z', this.velocity.z * dt, solids);
    this.moveVertical(this.velocity.y * dt, solids);
  }

  private moveHorizontal(axis: 'x' | 'z', delta: number, solids: AABB[]): void {
    if (Math.abs(delta) < 1e-8) return;
    const before = this.position[axis];
    this.position[axis] += delta;

    if (!this.overlapsAny(solids, this.torsoAABB())) return;

    const savedY = this.position.y;
    this.position.y += STEP_HEIGHT;
    if (!this.overlapsAny(solids, this.torsoAABB())) {
      const support = this.findSupportY(solids, savedY - SKIN, savedY + STEP_HEIGHT + SKIN);
      if (support !== null) {
        this.position.y = support + SKIN;
        this.velocity.y = 0;
        this.onGround = true;
        return;
      }
    }
    this.position.y = savedY;

    this.position[axis] = before;
    this.position[axis] += delta;
    const box = this.torsoAABB();
    for (const s of solids) {
      if (!this.overlaps(box, s)) continue;
      if (axis === 'x') {
        if (delta > 0) this.position.x = s.minX - RADIUS - SKIN;
        else this.position.x = s.maxX + RADIUS + SKIN;
        this.velocity.x = 0;
      } else {
        if (delta > 0) this.position.z = s.minZ - RADIUS - SKIN;
        else this.position.z = s.maxZ + RADIUS + SKIN;
        this.velocity.z = 0;
      }
      this.refreshTorso(box);
    }
  }

  private moveVertical(delta: number, solids: AABB[]): void {
    if (Math.abs(delta) < 1e-8) return;
    this.position.y += delta;
    const box = this.bodyAABB();
    let hitFloor = false;
    let hitCeil = false;

    for (const s of solids) {
      if (!this.overlaps(box, s)) continue;
      if (delta > 0) {
        this.position.y = s.minY - HEIGHT - SKIN;
        this.velocity.y = 0;
        hitCeil = true;
      } else {
        const feet = this.position.y;
        const onTop =
          feet <= s.maxY + GROUND_SNAP &&
          feet >= s.maxY - Math.max(Math.abs(delta) + SKIN * 2, 0.08) &&
          this.footprintOverlaps(s);
        if (onTop) {
          this.position.y = s.maxY + SKIN;
          this.velocity.y = 0;
          hitFloor = true;
        } else {
          this.resolveSideEscape(s);
        }
      }
      this.refreshBody(box);
    }

    if (hitFloor) this.onGround = true;
    else if (delta < 0 && !hitCeil) this.onGround = false;
  }

  private footprintOverlaps(s: AABB): boolean {
    const x0 = this.position.x - RADIUS * 0.85;
    const x1 = this.position.x + RADIUS * 0.85;
    const z0 = this.position.z - RADIUS * 0.85;
    const z1 = this.position.z + RADIUS * 0.85;
    return x1 > s.minX && x0 < s.maxX && z1 > s.minZ && z0 < s.maxZ;
  }

  private resolveSideEscape(s: AABB): void {
    const box = this.bodyAABB();
    if (!this.overlaps(box, s)) return;
    const overlapX = Math.min(box.maxX - s.minX, s.maxX - box.minX);
    const overlapZ = Math.min(box.maxZ - s.minZ, s.maxZ - box.minZ);
    const midX = (box.minX + box.maxX) * 0.5;
    const midZ = (box.minZ + box.maxZ) * 0.5;
    const smidX = (s.minX + s.maxX) * 0.5;
    const smidZ = (s.minZ + s.maxZ) * 0.5;
    if (overlapX < overlapZ) {
      this.position.x += midX < smidX ? -(overlapX + SKIN) : overlapX + SKIN;
      this.velocity.x = 0;
    } else {
      this.position.z += midZ < smidZ ? -(overlapZ + SKIN) : overlapZ + SKIN;
      this.velocity.z = 0;
    }
  }

  private findSupportY(solids: AABB[], minY: number, maxY: number): number | null {
    let best: number | null = null;
    for (const s of solids) {
      if (!this.footprintOverlaps(s)) continue;
      if (s.maxY < minY - 1e-4 || s.maxY > maxY + 1e-4) continue;
      const topThickness = s.maxY - s.minY;
      if (topThickness > 6 && s.maxY > this.position.y + STEP_HEIGHT + 0.5) continue;
      if (best === null || s.maxY > best) best = s.maxY;
    }
    return best;
  }

  private probeGround(solids: AABB[]): void {
    if (this.velocity.y > 0.12) {
      this.onGround = false;
      return;
    }

    let bestY = -Infinity;
    for (const s of solids) {
      if (!this.footprintOverlaps(s)) continue;
      const feet = this.position.y;
      if (s.maxY <= feet + GROUND_SNAP && s.maxY >= feet - GROUND_SNAP * 1.8) {
        bestY = Math.max(bestY, s.maxY);
      }
    }

    if (bestY > -Infinity) {
      const target = bestY + SKIN;
      if (Math.abs(this.position.y - target) > 1e-4) {
        this.position.y = target;
      }
      this.velocity.y = 0;
      this.onGround = true;
    } else if (this.onGround) {
      const pad = this.footProbeAABB();
      let supported = false;
      for (const s of solids) {
        if (
          pad.maxX > s.minX &&
          pad.minX < s.maxX &&
          pad.maxZ > s.minZ &&
          pad.minZ < s.maxZ &&
          Math.abs(pad.minY - s.maxY) < SKIN * 4
        ) {
          supported = true;
          break;
        }
      }
      if (!supported) this.onGround = false;
    }
  }

  private depenetrate(solids: AABB[]): void {
    for (let iter = 0; iter < 6; iter++) {
      const box = this.bodyAABB();
      let moved = false;
      for (const s of solids) {
        if (!this.overlaps(box, s)) continue;
        const overlapX = Math.min(box.maxX - s.minX, s.maxX - box.minX);
        const overlapY = Math.min(box.maxY - s.minY, s.maxY - box.minY);
        const overlapZ = Math.min(box.maxZ - s.minZ, s.maxZ - box.minZ);

        const feetNearTop =
          this.position.y >= s.maxY - STEP_HEIGHT && this.footprintOverlaps(s);

        if (feetNearTop && overlapY <= Math.max(overlapX, overlapZ) + 0.05) {
          this.position.y = s.maxY + SKIN;
          this.velocity.y = 0;
          this.onGround = true;
        } else if (overlapY <= overlapX && overlapY <= overlapZ) {
          if (box.minY + overlapY * 0.5 < (s.minY + s.maxY) * 0.5) {
            this.position.y = s.maxY + SKIN;
            this.onGround = true;
          } else {
            this.position.y = s.minY - HEIGHT - SKIN;
            this.velocity.y = Math.min(0, this.velocity.y);
          }
        } else if (overlapX <= overlapZ) {
          const mid = (box.minX + box.maxX) * 0.5;
          const smid = (s.minX + s.maxX) * 0.5;
          this.position.x += mid < smid ? -(overlapX + SKIN) : overlapX + SKIN;
          this.velocity.x = 0;
        } else {
          const mid = (box.minZ + box.maxZ) * 0.5;
          const smid = (s.minZ + s.maxZ) * 0.5;
          this.position.z += mid < smid ? -(overlapZ + SKIN) : overlapZ + SKIN;
          this.velocity.z = 0;
        }
        moved = true;
        this.refreshBody(box);
      }
      if (!moved) break;
    }
  }

  private bodyAABB(): AABB {
    return {
      minX: this.position.x - RADIUS,
      maxX: this.position.x + RADIUS,
      minY: this.position.y,
      maxY: this.position.y + HEIGHT,
      minZ: this.position.z - RADIUS,
      maxZ: this.position.z + RADIUS,
    };
  }

  private torsoAABB(): AABB {
    // Clear only a short step lip so debris / rail bases still block
    const clearance = Math.min(STEP_HEIGHT * 0.85, 0.22);
    return {
      minX: this.position.x - RADIUS,
      maxX: this.position.x + RADIUS,
      minY: this.position.y + clearance,
      maxY: this.position.y + HEIGHT - 0.12,
      minZ: this.position.z - RADIUS,
      maxZ: this.position.z + RADIUS,
    };
  }

  private footProbeAABB(): AABB {
    return {
      minX: this.position.x - RADIUS * 0.7,
      maxX: this.position.x + RADIUS * 0.7,
      minY: this.position.y - 0.02,
      maxY: this.position.y + 0.1,
      minZ: this.position.z - RADIUS * 0.7,
      maxZ: this.position.z + RADIUS * 0.7,
    };
  }

  private refreshBody(box: AABB): void {
    const b = this.bodyAABB();
    box.minX = b.minX;
    box.maxX = b.maxX;
    box.minY = b.minY;
    box.maxY = b.maxY;
    box.minZ = b.minZ;
    box.maxZ = b.maxZ;
  }

  private refreshTorso(box: AABB): void {
    const b = this.torsoAABB();
    box.minX = b.minX;
    box.maxX = b.maxX;
    box.minY = b.minY;
    box.maxY = b.maxY;
    box.minZ = b.minZ;
    box.maxZ = b.maxZ;
  }

  private overlaps(a: AABB, b: AABB): boolean {
    return (
      a.minX < b.maxX &&
      a.maxX > b.minX &&
      a.minY < b.maxY &&
      a.maxY > b.minY &&
      a.minZ < b.maxZ &&
      a.maxZ > b.minZ
    );
  }

  private overlapsAny(solids: AABB[], box: AABB): boolean {
    for (const s of solids) {
      if (this.overlaps(box, s)) return true;
    }
    return false;
  }

  private findClimb(climbables: Climbable[]): Climbable | null {
    const p = {
      x: this.position.x,
      y: this.position.y + HEIGHT * 0.45,
      z: this.position.z,
    };
    let best: Climbable | null = null;
    let bestDist = Infinity;
    for (const c of climbables) {
      if (!pointInAABB(p, c.aabb, 0.2)) continue;
      const dx = this.position.x - c.centerX;
      const dz = this.position.z - c.centerZ;
      const d = dx * dx + dz * dz;
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    return best;
  }

  nearestInteractable(list: Interactable[]): Interactable | null {
    for (const it of list) {
      if (it.used) continue;
      if (
        pointInAABB(
          { x: this.position.x, y: this.position.y + HEIGHT * 0.55, z: this.position.z },
          it.aabb,
          0.85,
        )
      ) {
        return it;
      }
    }
    return null;
  }
}
